import React, { useCallback, useEffect, useRef, useState } from "react";
import { useApolloClient, useMutation } from "@apollo/client";
import {
  GET_EMAIL_THREAD_IDS,
  GET_INC_EMAILS,
  GET_INC_EMAILS_SEARCH,
} from "../../../../utils/GraphQL/queries";
import "./emails.css";
import EmailModal from "../../adminComponents/EmailModal/EmailModal";
import SinglePictureLookup from "../../adminComponents/SinglePictureLookup/SinglePictureLookup";
import { useMediaQuery } from "@mui/material";
import { useSnackbar } from "../../../../utils/GlobalSnackbar/GlobalSnackbarContext";
import {
  ADD_BLOCKED_EMAIL,
  ARCHIVE_EMAIL,
  ARCHIVE_EMAILS,
  CHANGE_EMAILS_FLAG,
  DELETE_EMAIL,
  MARK_EMAILS_READ,
  ARCHIVE_EMAIL_CLEANUP,
  DELETE_EMAIL_THREAD,
  RESTORE_EMAIL_THREAD,
} from "../../../../utils/GraphQL/mutations";
import RestorePageIcon from "@mui/icons-material/RestorePage";
import CheckIcon from "@mui/icons-material/Check";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import SendIcon from "@mui/icons-material/Send";
import ArchiveIcon from "@mui/icons-material/Archive";
import ReplyIcon from "@mui/icons-material/Reply";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import InboxIcon from "@mui/icons-material/Inbox";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import ChecklistIcon from "@mui/icons-material/Checklist";
import DraftsIcon from "@mui/icons-material/Drafts";
import MarkunreadIcon from "@mui/icons-material/Markunread";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import OutlinedFlagIcon from "@mui/icons-material/OutlinedFlag";
import BdrButton from "../../../../components/BdrButton/BdrButton";
import BdrInput from "../../../../components/BdrInput/BdrInput";
import BdrCheckbox from "../../../../components/BdrCheckbox/BdrCheckbox";
import BdrMenu from "../../../../components/BdrMenu/BdrMenu";
import EmailRow from "../../adminComponents/EmailRow/EmailRow";
import SendEmailOpenBtn from "../../adminComponents/SendEmail/SendEmailOpenBtn";
import SendEmailModal from "../../adminComponents/SendEmail/SendEmailModal";
import EmailConfirmModal from "../../adminComponents/EmailConfirmModal/EmailConfirmModal";
import EmailFlags from "../../adminComponents/EmailFlags/EmailFlags";
import Auth from "../../../../utils/auth";
import { useSSEListener } from "../../../../utils/SSE/SSEContext";
import { hasAnyRole } from '../../../../utils/permissions';
import { lockBodyScroll, releaseBodyScroll } from "../../../../utils/bodyScrollLock";
import { PAGE_SIZE, searchCountLines, useEmailList } from "../../../../utils/emailSearch";

const NO_EMAILS = [];
// The thread count never blocks the click for long: the web link has no
// timeout of its own, so a hung count falls back to the one-message path.
const THREAD_COUNT_TIMEOUT_MS = 5000;
const AdminEmails = () => {
  const { handleSnack } = useSnackbar();
  const userProfile = Auth.getProfile()?.data;
  const userPermission = userProfile?.permission;
  const userEmailAddress = userProfile?.userEmailAddress;
  const [showMyInbox, setShowMyInbox] = useState(true);
  const [emailOpenId, setEmailOpenId] = useState("");
  const [searchInput, setSearchInput] = useState("");
  // Every list is 50 a page (D1); the page is part of the normal list's key.
  const [page, setPage] = useState(1);
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [openSendReply, setOpenSendReply] = useState(false);
  const [sendReplyObj, setSendReplyObj] = useState(false);
  const [picLookupOpen, setPicLookupOpen] = useState(false);
  const [openSearch, setOpenSearch] = useState(false);
  const [getArchived, setGetArchived] = useState([false, null, undefined]);
  // The list runs through client.query, not useLazyQuery: a lazy query's
  // promise settles only when its observable completes (useQuery.js
  // executeQuery), and executed from the list's effect it was seen never to
  // settle in the jsdom harness — the search would never land. client.query
  // is one request, one promise.
  const client = useApolloClient();
  const [deleteEmailThread] = useMutation(DELETE_EMAIL_THREAD);
  const [restoreEmailThread] = useMutation(RESTORE_EMAIL_THREAD);
  const [archiveEmail] = useMutation(ARCHIVE_EMAIL);
  const [blockEmailCall] = useMutation(ADD_BLOCKED_EMAIL);
  const [deleteEmail] = useMutation(DELETE_EMAIL);
  // Destructive-action confirm (delete / block) — both permanently delete the
  // email(s) + cascade their attached photos/PDFs, so they require confirmation.
  const [confirmAction, setConfirmAction] = useState(null); // {type,title,message,confirmLabel,emailId?,email?}
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [archiveCleanupCall] = useMutation(ARCHIVE_EMAIL_CLEANUP);
  /*
   * 900, not 600. The labels start colliding well before the phone
   * breakpoint — this header carries seven controls and the app is used in
   * a half-screen desktop window. Local rather than `BdrButton`'s own
   * `collapseToIcon`, which is hardcoded to 600px for every caller in the
   * app; changing that there would move every other page's threshold too.
   */
  const smaller900 = useMediaQuery("(max-width:900px)");
  /* The row overflow menu: { anchorEl, email }. ONE at a time for the whole
     list — a per-row piece of state would re-render every row on every
     open. */
  const [rowMenu, setRowMenu] = useState(null);
  // Multi-select mass actions (2026-09-14)
  // Select mode (2026-09-21): the bulk bar AND the per-row checkboxes are
  // hidden until "Select Many" is switched on, so the default list reads clean.
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [archiveEmailsCall] = useMutation(ARCHIVE_EMAILS);
  const [markEmailsReadCall] = useMutation(MARK_EMAILS_READ);
  const [changeEmailsFlagCall] = useMutation(CHANGE_EMAILS_FLAG);
  // The mailbox the list is showing, as the email queries take it. Built from
  // the KEY's mailbox, never this render's state, so an answer always matches
  // the view it was asked for.
  const mailboxVars = (myInbox) =>
    myInbox && userEmailAddress
      ? { userEmailAddress }
      : !myInbox
      ? { showAll: true }
      : {};
  const archivedView = getArchived[0] === true;

  // The list: one key from what the screen shows, one refresh() for every
  // fetch (utils/emailSearch.js, plan route R5). A search is 3+ letters and
  // runs in the database; 1–2 letters show a hint and fetch nothing.
  const list = useEmailList({
    text: openSearch ? searchInput : "",
    view: archivedView,
    mailbox: showMyInbox,
    page,
    runNormal: async ({ view, mailbox, page: p }) => {
      const { data, error } = await client.query({
        query: GET_INC_EMAILS,
        variables: {
          getArchived: view ? [true] : [false, null],
          page: String(p),
          limit: String(PAGE_SIZE),
          ...mailboxVars(mailbox),
        },
        fetchPolicy: "network-only",
      });
      return { emails: data?.getIncEmails?.emails, totalDoc: data?.getIncEmails?.totalDoc, error };
    },
    runSearch: async ({ term, view, mailbox }) => {
      const { data, error } = await client.query({
        query: GET_INC_EMAILS_SEARCH,
        variables: {
          getArchived: view ? [true] : [false, null],
          searchTerm: term,
          ...mailboxVars(mailbox),
        },
        fetchPolicy: "network-only",
      });
      return { emails: data?.getIncEmails?.emails, totalDoc: data?.getIncEmails?.totalDoc, error };
    },
  });
  const refresh = list.refresh;
  const searching = list.mode === "search";
  // What is on screen — the rows, the selection, the Select-all count and the
  // bulk bar all read this ONE list, never a hidden one. A failed normal fetch
  // keeps the last rows only when they are THIS screen's (D27) — never another
  // view's rows under this view's label and actions.
  const normalFailedKeepsRows =
    list.mode === "none" && list.state === "failed" && list.lastNormalIsCurrent;
  const displayed =
    list.state === "answered"
      ? list.answer.emails
      : normalFailedKeepsRows
      ? list.lastNormal.emails
      : NO_EMAILS;
  // The normal count: this page's answer, or — while another page of the SAME
  // list loads (pager disabled) or after it fails (pager enabled, so the user
  // can page back) — the last answer's numbers. Never another view's or
  // mailbox's numbers under this one's label (D27).
  const normalCount =
    list.mode === "none"
      ? list.state === "answered"
        ? list.answer
        : list.lastNormalSameList
        ? list.lastNormal
        : null
      : null;
  const totalDoc = Number(normalCount?.totalDoc) || 0;
  const highestPage = Math.max(1, Math.ceil(totalDoc / PAGE_SIZE));
  const pagerBusy = list.state === "pending";

  // SSE: silently refresh when another user triggers an email_update — through
  // a ref, so the handler (memoised once) always runs the CURRENT key.
  // ...but NOT while the search box is open (D4.4): new mail appears when you
  // close the search rather than during it.
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  const openSearchRef = useRef(openSearch);
  openSearchRef.current = openSearch;

  // Owner-inbox events (2026-10-09). The server scopes an `email_update` for
  // mail to the owner's inbox to admins and anyone it was also addressed to,
  // and tags it `scope: "owner"`. Such mail shows in exactly two views: the
  // admin "all" view (the reveal) and "my inbox" for someone on the To line.
  // Any other view cannot change, so it is not refetched. Refs, like the
  // search flag above, because the handler is memoised once.
  const showMyInboxRef = useRef(showMyInbox);
  showMyInboxRef.current = showMyInbox;
  const isAdminRef = useRef(false);
  isAdminRef.current = hasAnyRole(userPermission, ["admin"]);

  const handleEmailSSEUpdate = useCallback((data) => {
    if (openSearchRef.current) return;
    if (data?.scope === "owner" && !showMyInboxRef.current && !isAdminRef.current) return;
    refreshRef.current?.();
  }, []);
  useSSEListener("email_update", handleEmailSSEUpdate);
  useEffect(() => {
    window.scrollTo(0, 0);
    // eslint-disable-next-line
  }, []);
  // R4: the displayed list can change under a selection (search, SSE refetch,
  // page, view toggle). Prune to what is actually still on screen so a batch
  // can never carry an id the user can no longer see.
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(displayed.map((e) => e?.id));
      const next = new Set();
      prev.forEach((id) => {
        if (visible.has(id)) next.add(id);
      });
      return next.size === prev.size ? prev : next;
    });
  }, [displayed]);
  // A page / archived / my-inbox change, or a different search, is a different
  // view — drop the selection outright rather than carry ids across it.
  const searchKey = list.mode === "search" ? searchInput.trim() : list.mode;
  useEffect(() => {
    setSelectedIds(new Set());
  }, [page, getArchived, showMyInbox, searchKey]);

  /* Lock + release in the cleanup, so the pair is balanced by construction.
     The old shape released on every render where the modal was shut —
     including before it had ever opened — which drives a shared count
     negative. See utils/bodyScrollLock.js. */
  useEffect(() => {
    if (!emailModalOpen) return undefined;
    lockBodyScroll();
    return () => releaseBodyScroll();
  }, [emailModalOpen]);
  // ── Multi-select mass actions ───────────────────────────────────────────
  const displayedIds = displayed.map((e) => e?.id).filter(Boolean);
  const allSelected =
    displayedIds.length > 0 && displayedIds.every((id) => selectedIds.has(id));
  const someSelected = selectedIds.size > 0 && !allSelected;
  const bulkDisabled = bulkBusy || selectedIds.size === 0;
  const toggleSelected = (id) => {
    if (!id) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  };
  // Select all is THIS PAGE ONLY — never reach across pages.
  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(displayedIds));
  };
  // Leaving select mode DROPS the selection: the checkboxes go with it, and a
  // live selection you can no longer see is a trap (the next "Select Many"
  // would re-open holding ids the user forgot about).
  const toggleSelectMode = () => {
    if (selectMode && selectedIds.size > 0) setSelectedIds(new Set());
    setSelectMode((prev) => !prev);
  };
  // The server caps a batch at 200 ids. A page is 50 and a search returns at
  // most 200, so one batch fits today — the split stays as a guard.
  const CHUNK_SIZE = 200;
  const chunkIds = (ids) => {
    const out = [];
    for (let i = 0; i < ids.length; i += CHUNK_SIZE) {
      out.push(ids.slice(i, i + CHUNK_SIZE));
    }
    return out;
  };
  const runBulkOn = async (ids, mutate, buildVars, resultKey) => {
    if (!ids?.length || bulkBusy) return null;
    setBulkBusy(true);
    try {
      let count = 0;
      for (const chunk of chunkIds(ids)) {
        const { data } = await mutate({ variables: buildVars(chunk) });
        count += parseInt(data?.[resultKey], 10) || 0;
      }
      return { ids, count };
    } catch (err) {
      handleSnack("Something went wrong...", 3000);
      return null;
    } finally {
      setBulkBusy(false);
    }
  };
  // A short count means the server refused some ids (not visible to this user).
  const bulkSnack = (count, total, verb) => {
    handleSnack(
      count < total
        ? `${count} of ${total} ${verb} (some weren't visible to you)`
        : `${count} ${verb}`,
      3000
    );
  };
  const handleBulkRead = async (read) => {
    // Snapshot BEFORE the first await — selectedIds is live state.
    const res = await runBulkOn(
      [...selectedIds],
      markEmailsReadCall,
      (chunk) => ({ emailIds: chunk, read }),
      "markEmailsRead"
    );
    if (!res) return;
    bulkSnack(res.count, res.ids.length, read ? "marked read" : "marked unread");
    setSelectedIds(new Set());
    refresh();
  };
  const handleBulkFlag = async (flag) => {
    const res = await runBulkOn(
      [...selectedIds],
      changeEmailsFlagCall,
      (chunk) => ({ emailIds: chunk, flag }),
      "changeEmailsFlag"
    );
    if (!res) return;
    bulkSnack(res.count, res.ids.length, flag === "" ? "unflagged" : "flagged");
    setSelectedIds(new Set());
    refresh();
  };
  // D5: undo is the same mutation with the flag flipped, over the SAME snapshot.
  const handleBulkRestore = async (ids) => {
    const res = await runBulkOn(
      ids,
      archiveEmailsCall,
      (chunk) => ({ emailIds: chunk, changeArchive: false }),
      "archiveEmails"
    );
    if (!res) return;
    handleSnack(`${res.count} Restored`, 2500);
    refresh();
  };
  const handleBulkArchive = async (changeArchive) => {
    const res = await runBulkOn(
      [...selectedIds],
      archiveEmailsCall,
      (chunk) => ({ emailIds: chunk, changeArchive }),
      "archiveEmails"
    );
    if (!res) return;
    setSelectedIds(new Set());
    refresh();
    if (!changeArchive) {
      bulkSnack(res.count, res.ids.length, "restored");
      return;
    }
    const label =
      res.count < res.ids.length
        ? `${res.count} of ${res.ids.length} archived (some weren't visible to you)`
        : `${res.count} Archived`;
    handleSnack(
      <div className="email-archive-snack">
        <span className="email-archive-item-snack">{label}</span>
        <button
          className="email-archive-snack-btn"
          onClick={() => {
            handleBulkRestore(res.ids);
          }}
        >
          <RestorePageIcon sx={{ fontSize: "20px", color: "#05F842" }} />
          (Restore?)
        </button>
      </div>,
      8000
    );
  };
  const handlePage = (direction) => {
    if (direction === "up") setPage((p) => Math.min(p + 1, highestPage));
    if (direction === "down") setPage((p) => Math.max(p - 1, 1));
  };
  const openEmail = (id) => {
    setEmailOpenId(id);
    setEmailModalOpen(true);
  };
  const changeParentStyle = (bool) => {
    setSendModalOpen(bool);
  };
  const prepareReplyEmail = (emailObj, emailObjIsSentObjBool) => {
    // if email obj is a sent obj, the reply to address needs to be the 'to' address of sent obj 
    const sendTo = emailObjIsSentObjBool ? emailObj?.to : emailObj?.from;
    setSendReplyObj({
      to: sendTo,
      subject: `Re: ${emailObj?.emailSubject}`,
    });
    if (emailModalOpen) {
      closeEmail();
    }
    setOpenSendReply(true);
  };
  const changeParentForEmailModal = (bool) => {
    setOpenSendReply(bool);
  };
  // The view and page 1 change together, in one handler, so the list's key
  // changes once — never a page reset racing a fetch.
  const setArchiveRetrieval = (showArchived) => {
    setGetArchived(showArchived ? [true] : [false, null, undefined]);
    setPage(1);
  };
  const closeEmail = (event) => {
    setEmailOpenId("");
    setEmailModalOpen(false);
    refresh();
  };
  const handleArchive = async (emailId) => {
    const { data: archived } = await archiveEmail({
      variables: { emailId: emailId, changeArchive: true },
    });
    if (archived?.archiveEmail) {
      handleSnack(
        <div className="email-archive-snack">
          <span className="email-archive-item-snack">Email Archived</span>
          <button
            className="email-archive-snack-btn"
            onClick={() => {
              handleRestoreArchive(emailId);
            }}
          >
            <RestorePageIcon sx={{ fontSize: "20px", color: "#05F842" }} />
            (Restore?)
          </button>
        </div>,
        8000
      );
      refresh();
    }
    return;
  };
  const handleRestoreArchive = async (emailId) => {
    const { data: archived } = await archiveEmail({
      variables: { emailId: emailId, changeArchive: false },
    });
    if (archived?.archiveEmail) {
      handleSnack(
        <div className="email-archive-snack">
          <span className="email-archive-item-snack">Email Restored </span>
          <CheckIcon sx={{ fontSize: "20px", color: "#05F842" }} />
        </div>,
        2500
      );
      refresh();
    }
    return;
  };
  const handleDeleteEmail = async (emailId) => {
    const { data: deleted } = await deleteEmail({
      variables: { emailId: emailId },
    });
    if (deleted?.deleteEmailById) {
      handleSnack(
        <div className="email-archive-snack">
          <span className="email-archive-item-snack">Email Deleted </span>
          <CheckIcon sx={{ fontSize: "20px", color: "#05F842" }} />
        </div>,
        2000
      );
      refresh();
    }
    return;
  };
  const archiveCleanup = async () => {
    const { data } = await archiveCleanupCall({
      variables: {
        current: Date.now().toString(),
      },
    });
    if (data?.archivedEmailCleanup) {
      handleSnack(
        `Archives Cleaned Up. Deleted: ${data?.archivedEmailCleanup}`,
        2000
      );
      refresh();
    }
    return;
  };
  // The archived thread this row stands for (the row + what is folded under
  // it, in this mailbox view), or null when it can't be counted — an error, an
  // old server, or no answer within THREAD_COUNT_TIMEOUT_MS. null and one id
  // both mean "just this message".
  const countThread = (emailId) =>
    Promise.race([
      client
        .query({
          query: GET_EMAIL_THREAD_IDS,
          variables: { emailId, ...mailboxVars(showMyInbox) },
          fetchPolicy: "network-only",
        })
        .then(
        ({ data, error }) => (error ? null : data?.getEmailThreadIds ?? null),
        () => null
      ),
      new Promise((resolve) => setTimeout(() => resolve(null), THREAD_COUNT_TIMEOUT_MS)),
    ]);
  const DELETE_ONE_MESSAGE =
    "This permanently deletes the email and any attached photos or PDFs that aren't used anywhere else. This can't be undone.";
  // Open the gradient confirm modal for a delete. It opens at once with only
  // "this message"; the whole-thread choice joins it when the count arrives
  // (D19d — and only when the thread is more than this one message).
  const requestDeleteEmail = (emailId) => {
    setConfirmAction({
      type: "delete",
      emailId,
      title: "Delete this email?",
      message: DELETE_ONE_MESSAGE,
      confirmLabel: "Delete this message",
      secondaryLabel: "Counting the thread…",
      secondaryDisabled: true,
    });
    countThread(emailId).then((ids) => {
      setConfirmAction((current) => {
        if (current?.type !== "delete" || current.emailId !== emailId) return current;
        if (!ids || ids.length <= 1) {
          return { ...current, secondaryLabel: null, secondaryDisabled: false };
        }
        return {
          ...current,
          threadIds: ids,
          message: `Delete this message, or all ${ids.length} archived messages in this thread? Deleting removes them and any attached photos or PDFs that aren't used anywhere else. This can't be undone.`,
          secondaryLabel: `Delete whole thread (${ids.length})`,
          secondaryDisabled: false,
        };
      });
    });
  };
  // Restore: one message restores at once, as before (D19e); a thread asks.
  // A second click while this email is being counted or restored is ignored —
  // it would count again and restore twice (sign-off F10).
  const restoreCounting = useRef(new Set());
  const requestRestoreEmail = async (emailId) => {
    if (restoreCounting.current.has(emailId)) return;
    restoreCounting.current.add(emailId);
    try {
      const ids = await countThread(emailId);
      if (!ids || ids.length <= 1) {
        await handleRestoreArchive(emailId);
        return;
      }
      setConfirmAction({
        type: "restore",
        tone: "neutral",
        emailId,
        threadIds: ids,
        title: "Restore to inbox?",
        message: `Restore this message, or all ${ids.length} archived messages in this thread?`,
        confirmLabel: "Restore this message",
        secondaryLabel: `Restore whole thread (${ids.length})`,
      });
    } finally {
      restoreCounting.current.delete(emailId);
    }
  };
  const runThreadAction = async (action) => {
    const { type, emailId, threadIds } = action;
    const variables = { emailId, emailIds: threadIds, ...mailboxVars(showMyInbox) };
    try {
      if (type === "delete") {
        const { data } = await deleteEmailThread({ variables });
        const n = Number(data?.deleteEmailThread) || 0;
        handleSnack(n > 0 ? `${n} deleted` : "Already deleted", 2500);
      } else {
        const { data } = await restoreEmailThread({ variables });
        const n = Number(data?.restoreEmailThread) || 0;
        handleSnack(n > 0 ? `${n} restored` : "Already restored", 2500);
      }
    } catch (err) {
      handleSnack("Something went wrong...", 3000);
    }
    refresh();
  };
  // Open the gradient confirm modal for a block (blocks the sender AND wipes
  // every email from them + their attachments).
  const requestBlockEmail = (email) => {
    const strippedEmail = email.split("<").pop().split(">")[0].trim();
    setConfirmAction({
      type: "block",
      email,
      title: `Block ${strippedEmail}?`,
      message:
        "This blocks the sender and permanently deletes every email from them — including any attached photos or PDFs not used elsewhere. This can't be undone.",
      confirmLabel: "Block & delete",
    });
  };
  // Open the gradient confirm modal for the bulk archive cleanup (deletes ALL
  // archived emails older than 60 days + their attachments).
  const requestArchiveCleanup = () => {
    setConfirmAction({
      type: "archiveCleanup",
      title: "Clean up archived emails?",
      message:
        "This permanently deletes every archived email older than 60 days — and any attached photos or PDFs not used elsewhere. This is a bulk action and can't be undone.",
      confirmLabel: "Delete archives",
    });
  };
  const blockEmail = async (email) => {
    const { data } = await blockEmailCall({
      variables: {
        incEmail: email,
      },
    });
    if (data?.addBlockedEmail) {
      handleSnack(
        <div className="email-archive-snack">
          <span className="email-archive-item-snack">
            Email Blocked. {data?.addBlockedEmail?.emailsReceived} Emails
            Deleted
          </span>
          <CheckIcon sx={{ fontSize: "20px", color: "#05F842" }} />
        </div>,
        3000
      );
      refresh();
      return;
    }
    handleSnack("Something went wrong...", 2000);
    return;
  };
  // Run the confirmed destructive action (delete or block), then close the modal.
  const handleConfirmAction = async () => {
    if (!confirmAction) return;
    setConfirmBusy(true);
    try {
      if (confirmAction.type === "delete") {
        await handleDeleteEmail(confirmAction.emailId);
      } else if (confirmAction.type === "restore") {
        await handleRestoreArchive(confirmAction.emailId);
      } else if (confirmAction.type === "block") {
        await blockEmail(confirmAction.email);
      } else if (confirmAction.type === "archiveCleanup") {
        await archiveCleanup();
      }
    } finally {
      setConfirmBusy(false);
      setConfirmAction(null);
    }
  };
  // The confirm's second button: the whole thread.
  const handleSecondaryAction = async () => {
    if (!confirmAction?.threadIds) return;
    setConfirmBusy(true);
    try {
      await runThreadAction(confirmAction);
    } finally {
      setConfirmBusy(false);
      setConfirmAction(null);
    }
  };
  const loadingHolders = () => {
    return Array.from({ length: 15 }).map((_, index) => (
      <EmailRow
        key={index + Date.now()}
        loading
        /* Passing the slot at all is what reserves the checkbox column, so the
           list does not jump when the real rows land in select mode. */
        selectSlot={selectMode ? true : undefined}
      />
    ));
  };

  return (
    <div className="email-page">
      {emailModalOpen && (
        <EmailModal
          prepareReply={prepareReplyEmail}
          emailId={emailOpenId}
          closeEmail={closeEmail}
          changeEmail={openEmail}
        />
      )}
      {/* Send email modal for replies */}
      {openSendReply && (
        <SendEmailModal
          changeParent={changeParentForEmailModal}
          templateObj={sendReplyObj}
        />
      )}
      {picLookupOpen && <SinglePictureLookup />}
      <EmailConfirmModal
        open={!!confirmAction}
        title={confirmAction?.title}
        message={confirmAction?.message}
        confirmLabel={confirmAction?.confirmLabel}
        secondaryLabel={confirmAction?.secondaryLabel}
        secondaryDisabled={confirmAction?.secondaryDisabled}
        onSecondary={handleSecondaryAction}
        tone={confirmAction?.tone}
        busy={confirmBusy}
        onCancel={() => (confirmBusy ? null : setConfirmAction(null))}
        onConfirm={handleConfirmAction}
      />
      <div
        className={
          sendModalOpen
            ? "email-page-container bdr-section-panel"
            : "email-page-container bdr-section-panel email-page-filter"
        }
      >
        {/* The blue band: title, counts, paging and every control.
            Opaque on purpose — `.bdr-section-panel-hdr` has to occlude
            what scrolls under it, which a translucent lift cannot do. */}
        <div className="bdr-section-panel-hdr email-page-hdr">
          <h2 className="bdr-section-panel-title">
            {getArchived[0] ? "Viewing Archived" : "Viewing Inbox"}
          </h2>
          {/* The count line keeps its place while it is empty (a short term,
              a search loading or failed), so the search box under the header
              never jumps. */}
          <div className="email-page-page-box" role="status">
            {list.state === "answered" && searching &&
              searchCountLines(list.answer.totalDoc).map((line) => (
                <span key={line} className="email-page-count-line">{line}</span>
              ))}
            {list.mode === "none" && normalCount && totalDoc > 0 && (
              <>
                <span style={{ margin: "0px 5px" }}>
                  {totalDoc} {totalDoc > 1 ? "Emails" : "Email"} - Page {page} of{" "}
                  {highestPage}
                </span>
                {page !== 1 && (
                  <div className="email-page-page-control-button-box">
                    <button
                      disabled={pagerBusy}
                      className="email-page-page-control-button"
                      aria-label="Previous page"
                      onClick={() => handlePage("down")}
                    >
                      <strong>-</strong>
                    </button>
                  </div>
                )}
                {page < highestPage && (
                  <div className="email-page-page-control-button-box">
                    <button
                      disabled={pagerBusy}
                      className="email-page-page-control-button"
                      aria-label="Next page"
                      onClick={() => handlePage("up")}
                    >
                      <strong>+</strong>
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
          {/*
            Every control here is `BdrButton variant="action"` — the tinted
            semi-transparent blue — and every one collapses to its icon at the
            SAME width, so the row never half-collapses into a mix of labels and
            glyphs. `iconOnly` is driven by the page's own 900px query rather
            than `collapseToIcon`, which is pinned to 600 inside BdrButton.
          */}
          <div className="email-actions-button-box">
            {hasAnyRole(userPermission, ["admin", "office"]) && (
              <BdrButton
                variant="action"
                icon={<InboxIcon />}
                iconOnly={smaller900}
                onClick={() => setShowMyInbox((prev) => !prev)}
                title={
                  showMyInbox
                    ? "Show All"
                    : userEmailAddress
                    ? "Show My Inbox"
                    : "Hide Private"
                }
              >
                {showMyInbox
                  ? "Show All"
                  : userEmailAddress
                  ? "Show My Inbox"
                  : "Hide Private"}
              </BdrButton>
            )}
            {getArchived[0] && (
              <BdrButton
                variant="action"
                icon={<RestorePageIcon />}
                iconOnly={smaller900}
                onClick={requestArchiveCleanup}
                title="Cleanup Archives"
              >
                Cleanup Archives
              </BdrButton>
            )}
            <SendEmailOpenBtn
              changeParentStyle={changeParentStyle}
              icon={<SendIcon />}
              iconOnly={smaller900}
              sendEmailBtnMes="Send Email"
            />
            <BdrButton
              variant="action"
              icon={<SearchIcon />}
              iconOnly={smaller900}
              onClick={() => {
                if (openSearch) {
                  setSearchInput("");
                  // Catch up on the email_update events skipped while the box
                  // was open (D4.4) — closing an EMPTY box changes no key, so
                  // nothing else would refetch (sign-off F1).
                  refresh();
                }
                setOpenSearch((prev) => !prev);
              }}
              title={openSearch ? "Close Search" : "Search Emails"}
            >
              {openSearch ? "Close Search" : "Search Emails"}
            </BdrButton>
            <BdrButton
              variant="action"
              icon={getArchived[0] ? <InboxIcon /> : <ArchiveIcon />}
              iconOnly={smaller900}
              onClick={() => setArchiveRetrieval(!getArchived[0])}
              title={getArchived[0] ? "Show Inbox" : "Show Archived"}
            >
              {getArchived[0] ? "Show Inbox" : "Show Archived"}
            </BdrButton>
            <BdrButton
              variant="action"
              icon={<ChecklistIcon />}
              iconOnly={smaller900}
              className={selectMode ? "email-select-many-on" : ""}
              ariaLabel={selectMode ? "Close Select" : "Select Many"}
              title={selectMode ? "Close Select" : "Select Many"}
              onClick={toggleSelectMode}
            >
              {selectMode ? "Close Select" : "Select Many..."}
            </BdrButton>
            {/* These two have never had a label — `iconOnly` at every width. */}
            <BdrButton
              variant="action"
              iconOnly
              icon={<CameraAltIcon />}
              ariaLabel="Picture Lookup"
              title="Picture Lookup"
              onClick={() => {
                setPicLookupOpen((prev) => !prev);
              }}
            />
            <BdrButton
              variant="action"
              iconOnly
              icon={<RefreshIcon />}
              ariaLabel="Refresh Emails"
              title="Refresh Emails"
              onClick={() => refresh()}
            />
          </div>
          {openSearch && (
            <div className="email-page-search-box">
              <div className="email-page-search-input">
                <BdrInput
                  type="text"
                  label={`Search`}
                  name="to"
                  inputMode="text"
                  autoFocus
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  loading={searching && list.state === "pending"}
                />
              </div>
            </div>
          )}
        </div>

        {/* The gray body the blue rows sit on. */}
        <div className="bdr-section-panel-body email-page-body">
          {selectMode && displayed.length > 0 && (
            <div className="email-bulk-bar">
              <BdrCheckbox
                className="email-bulk-select-all"
                checked={allSelected}
                indeterminate={someSelected}
                disabled={bulkBusy}
                onChange={toggleSelectAll}
                ariaLabel={
                  searching
                    ? `Select all ${displayed.length} results`
                    : `Select all ${displayed.length} on this page`
                }
                label={
                  selectedIds.size > 0
                    ? `${selectedIds.size} selected`
                    : searching
                    ? `Select all ${displayed.length} results`
                    : `Select all ${displayed.length} on this page`
                }
              />
              {/* `action-solid`, not `action`: these sit ON the panel gradient,
                  where the subdued tint has almost nothing to read against.
                  Same 900px collapse as the header row. */}
              <div className="email-bulk-actions">
                <BdrButton
                  variant="action-solid"
                  icon={<DraftsIcon />}
                  iconOnly={smaller900}
                  disabled={bulkDisabled}
                  title="Mark Read"
                  onClick={() => handleBulkRead(true)}
                >
                  Mark Read
                </BdrButton>
                <BdrButton
                  variant="action-solid"
                  icon={<MarkunreadIcon />}
                  iconOnly={smaller900}
                  disabled={bulkDisabled}
                  title="Mark Unread"
                  onClick={() => handleBulkRead(false)}
                >
                  Mark Unread
                </BdrButton>
                <BdrButton
                  variant="action-solid"
                  icon={<PriorityHighIcon />}
                  iconOnly={smaller900}
                  disabled={bulkDisabled}
                  title="Flag Important"
                  onClick={() => handleBulkFlag("!")}
                >
                  Important
                </BdrButton>
                <BdrButton
                  variant="action-solid"
                  icon={<ReportProblemIcon />}
                  iconOnly={smaller900}
                  disabled={bulkDisabled}
                  title="Flag Urgent"
                  onClick={() => handleBulkFlag("!!")}
                >
                  Urgent
                </BdrButton>
                <BdrButton
                  variant="action-solid"
                  icon={<OutlinedFlagIcon />}
                  iconOnly={smaller900}
                  disabled={bulkDisabled}
                  title="Remove Flag"
                  onClick={() => handleBulkFlag("")}
                >
                  Remove Flag
                </BdrButton>
                {getArchived[0] ? (
                  <BdrButton
                    variant="action-solid"
                    icon={<RestorePageIcon />}
                    iconOnly={smaller900}
                    disabled={bulkDisabled}
                    title="Restore"
                    onClick={() => handleBulkArchive(false)}
                  >
                    Restore
                  </BdrButton>
                ) : (
                  <BdrButton
                    variant="action-solid"
                    icon={<ArchiveIcon />}
                    iconOnly={smaller900}
                    disabled={bulkDisabled}
                    title="Archive"
                    onClick={() => handleBulkArchive(true)}
                  >
                    Archive
                  </BdrButton>
                )}
              </div>
            </div>
          )}
          {list.state === "pending" && loadingHolders()}
          {list.state === "short" && (
            <p className="bdr-empty">Type at least 3 letters to search</p>
          )}
          {list.state === "failed" && (searching || !normalFailedKeepsRows) && (
            <div className="bdr-alert bdr-alert--danger email-search-failed" role="alert">
              <span>{searching ? "Search didn't load — try again." : "Emails didn't load — try again."}</span>
              <BdrButton variant="action-solid" onClick={() => refresh()}>
                Try again
              </BdrButton>
            </div>
          )}
          {displayed.length > 0 ? (
            displayed.map((email, index) => {
              /* Date, sender parsing and the read/flag state classes all live
                 in `EmailRow` now — three hosts were deriving them identically
                 and had already drifted twice. */
              return (
                <EmailRow
                  key={"emails-number:" + email?.id + index}
                  email={email}
                  onOpen={() => openEmail(email?.id)}
                  selectSlot={
                    selectMode ? (
                      // The span, not the checkbox, carries the stopPropagation:
                      // the row behind this opens the email on click.
                      <span
                        className="email-select-box"
                        onClick={(event) => event.stopPropagation()}
                      >
                        <BdrCheckbox
                          checked={selectedIds.has(email?.id)}
                          disabled={bulkBusy}
                          onChange={(event) => {
                            event.stopPropagation();
                            toggleSelected(email?.id);
                          }}
                          ariaLabel={`Select email from ${
                            email?.from || "unknown sender"
                          }`}
                        />
                      </span>
                    ) : undefined
                  }
                  actions={
                    <>
                      {/* Revealed on hover / focus-visible. The overflow and the
                          flag stay put, so every row keeps a visible way in. */}
                      <span className="email-row-quick">
                        <button
                          className="email-row-btn"
                          title="Reply"
                          onClick={(event) => {
                            event.stopPropagation();
                            prepareReplyEmail(email, false);
                          }}
                        >
                          <ReplyIcon sx={{ fontSize: "18px" }} />
                        </button>
                        {!getArchived[0] && (
                          <button
                            className="email-row-btn"
                            title="Archive"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleArchive(email?.id);
                            }}
                          >
                            <ArchiveIcon sx={{ fontSize: "18px" }} />
                          </button>
                        )}
                      </span>
                      <EmailFlags
                        emailRead={email?.emailRead}
                        getSelectedEmails={refresh}
                        handleSnack={handleSnack}
                        emailId={email?.id}
                      />
                      <button
                        className="email-row-btn"
                        title="More actions"
                        aria-label="More actions"
                        onClick={(event) => {
                          event.stopPropagation();
                          setRowMenu({ anchorEl: event.currentTarget, email });
                        }}
                      >
                        <MoreHorizIcon sx={{ fontSize: "18px" }} />
                      </button>
                    </>
                  }
                />
              );
            })
          ) : (
            /*
             * `.bdr-empty`, not `.page-header`. That class is a gradient-filled
             * card with a heavy text shadow — it reads as a button sitting in
             * the middle of the list, which is the opposite of what an empty
             * state should look like. The global recipe is italic and muted:
             * clearly a STATE, not a record.
             *
             * And it says which empty it is. "No Emails To Display" was the
             * same sentence whether the mailbox was empty, the archive was
             * empty, or a search simply missed — three different situations,
             * only one of which means anything is wrong.
             */
            list.state === "answered" && (
              <p className="bdr-empty">
                {searching
                  ? "No emails match that search."
                  : getArchived[0]
                  ? "Nothing in the archive."
                  : "No emails in the inbox."}
              </p>
            )
          )}
        </div>
      </div>

      {/*
        ONE menu for the whole list, anchored to whichever row opened it.
        It carries the actions the quick rail drops at narrow widths plus the
        two that never belonged on a resting row — Block, and Delete in the
        archived view. Every entry here is reachable with the quick rail
        hidden, which is what makes hiding it safe.
      */}
      <BdrMenu
        anchorEl={rowMenu?.anchorEl || null}
        onClose={() => setRowMenu(null)}
        ariaLabel="Email actions"
        items={[
          {
            key: "reply",
            label: "Reply",
            onClick: () => prepareReplyEmail(rowMenu?.email, false),
          },
          getArchived[0]
            ? {
                key: "restore",
                label: "Restore to inbox",
                onClick: () => requestRestoreEmail(rowMenu?.email?.id),
              }
            : {
                key: "archive",
                label: "Archive",
                onClick: () => handleArchive(rowMenu?.email?.id),
              },
          { key: "d1", divider: true },
          {
            key: "block",
            label: "Block sender",
            danger: true,
            onClick: () => requestBlockEmail(rowMenu?.email?.from),
          },
          getArchived[0] && {
            key: "delete",
            label: "Delete permanently",
            danger: true,
            onClick: () => requestDeleteEmail(rowMenu?.email?.id),
          },
        ]}
      />
    </div>
  );
};
export default AdminEmails;
